import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Target, Heart, ArrowRight, ArrowDown, MapPin } from 'lucide-react';
import PortfolioTeaser from '../components/PortfolioTeaser';
import BuildWithUs from '../components/BuildWithUs';

interface TrailBlob {
  id: number;
  x: number;
  y: number;
  scale: number;
  opacity: number;
  rotation: number;
  variant: number; // 0-2 for different splatter shapes
}

const PersonalLanding: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageAreaRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  const portfolioRef = useRef<HTMLAnchorElement>(null);
  const socialRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [blobPos, setBlobPos] = useState({ x: 0, y: 0 });
  const [trails, setTrails] = useState<TrailBlob[]>([]);
  const [isHovering, setIsHovering] = useState(false);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [blobScale, setBlobScale] = useState(1);
  const [inversions, setInversions] = useState({
    name: false,
    portfolio: false,
    social: false,
    hint: false
  });
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [showMobileHint, setShowMobileHint] = useState(true);
  const [logoHovered, setLogoHovered] = useState(false);
  const [socialHovered, setSocialHovered] = useState<'x' | 'github' | null>(null);
  const [isTouchingImage, setIsTouchingImage] = useState(false);

  // Detect coarse-pointer devices (phones/tablets). Avoid maxTouchPoints —
  // Windows laptops often report touch capability and would incorrectly
  // disable the mouse-driven reveal.
  useEffect(() => {
    const coarsePointer = window.matchMedia('(pointer: coarse)');
    const updateTouchMode = () => setIsTouchDevice(coarsePointer.matches);
    updateTouchMode();
    coarsePointer.addEventListener('change', updateTouchMode);
    return () => coarsePointer.removeEventListener('change', updateTouchMode);
  }, []);

  // Responsive blob scaling
  useEffect(() => {
    const updateBlobScale = () => {
      const width = window.innerWidth;
      // Scale from 1.0 at 1440px down to 0.5 at 400px
      const scale = Math.max(0.5, Math.min(1, (width - 400) / 1040 * 0.5 + 0.5));
      setBlobScale(scale);
    };
    updateBlobScale();
    window.addEventListener('resize', updateBlobScale);
    return () => window.removeEventListener('resize', updateBlobScale);
  }, []);

  // Show sticky nav when scrolled past hero
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      setShowStickyNav(window.scrollY > heroHeight - 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const lastMousePos = useRef({ x: 0, y: 0 });
  const trailIdRef = useRef(0);
  const animationFrameRef = useRef<number>();

  // Check if blob overlaps with an element
  const checkOverlap = useCallback((elementRef: React.RefObject<HTMLElement | null>, blobRadius = 100) => {
    if (!elementRef.current || !containerRef.current) return false;

    const containerRect = containerRef.current.getBoundingClientRect();
    const elementRect = elementRef.current.getBoundingClientRect();

    const elementLeft = elementRect.left - containerRect.left;
    const elementRight = elementRect.right - containerRect.left;
    const elementTop = elementRect.top - containerRect.top;
    const elementBottom = elementRect.bottom - containerRect.top;

    const closestX = Math.max(elementLeft, Math.min(blobPos.x, elementRight));
    const closestY = Math.max(elementTop, Math.min(blobPos.y, elementBottom));

    const distanceX = blobPos.x - closestX;
    const distanceY = blobPos.y - closestY;
    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

    return distance < blobRadius;
  }, [blobPos]);

  // Throttle overlap checking on mobile
  const lastOverlapCheck = useRef(0);
  useEffect(() => {
    if (!isHovering) {
      setInversions({ name: false, portfolio: false, social: false, hint: false });
      return;
    }

    // Throttle on mobile - only check every 100ms
    if (isTouchDevice) {
      const now = Date.now();
      if (now - lastOverlapCheck.current < 100) return;
      lastOverlapCheck.current = now;
    }

    setInversions({
      name: checkOverlap(nameRef),
      portfolio: checkOverlap(portfolioRef),
      social: checkOverlap(socialRef),
      hint: checkOverlap(hintRef)
    });
  }, [blobPos, isHovering, checkOverlap, isTouchDevice]);

  useEffect(() => {
    // While actively touching, positions are written directly — skip lerp
    if (isTouchingImage) return;

    // Smooth lerp for mouse (and hybrid devices that incorrectly report touch)
    const animate = () => {
      setBlobPos(prev => ({
        x: prev.x + (mousePos.x - prev.x) * 0.85,
        y: prev.y + (mousePos.y - prev.y) * 0.85
      }));
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mousePos, isTouchingImage]);

  useEffect(() => {
    if (!isHovering) return;
    // Fewer trails on touch devices for performance
    const maxTrails = isTouchDevice ? 3 : 6;

    const dx = mousePos.x - lastMousePos.current.x;
    const dy = mousePos.y - lastMousePos.current.y;
    const speed = Math.sqrt(dx * dx + dy * dy);

    // Higher speed threshold on mobile to reduce trail frequency
    const speedThreshold = isTouchDevice ? 15 : 8;
    if (speed > speedThreshold) {
      const newTrail: TrailBlob = {
        id: trailIdRef.current++,
        x: blobPos.x,
        y: blobPos.y,
        scale: Math.min(speed / 40, 0.9),
        opacity: 0.65,
        rotation: Math.random() * 360,
        variant: Math.floor(Math.random() * 3)
      };
      setTrails(prev => [...prev.slice(-maxTrails), newTrail]);
    }

    lastMousePos.current = { ...mousePos };
  }, [mousePos, blobPos, isHovering, isTouchDevice]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTrails(prev =>
        prev
          .map(trail => ({
            ...trail,
            opacity: trail.opacity - 0.08,
            scale: trail.scale * 0.92
          }))
          .filter(trail => trail.opacity > 0)
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePos({ x, y });

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const parallaxX = (x - centerX) / centerX * -15;
    const parallaxY = (y - centerY) / centerY * -15;
    setParallax({ x: parallaxX, y: parallaxY });
  }, []);

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => {
    setIsHovering(false);
    setTrails([]);
  };

  // Helper function to check if touch point is within the image area
  const isTouchInImageArea = useCallback((touchX: number, touchY: number): boolean => {
    if (!imageAreaRef.current || !containerRef.current) return false;

    const containerRect = containerRef.current.getBoundingClientRect();
    const imageRect = imageAreaRef.current.getBoundingClientRect();

    // Convert touch coordinates to be relative to viewport
    const absoluteX = touchX + containerRect.left;
    const absoluteY = touchY + containerRect.top;

    // Check if touch is within image bounds (with some padding for the blob effect)
    const padding = 50;
    return (
      absoluteX >= imageRect.left - padding &&
      absoluteX <= imageRect.right + padding &&
      absoluteY >= imageRect.top - padding &&
      absoluteY <= imageRect.bottom + padding
    );
  }, []);

  // Touch event handlers for mobile - using refs to track state in native event listeners
  const isTouchingImageRef = useRef(false);

  // Sync state with ref
  useEffect(() => {
    isTouchingImageRef.current = isTouchingImage;
  }, [isTouchingImage]);

  // Native touch event handlers with passive: false to enable preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isTouchDevice) return;

    const handleNativeTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const rect = container.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Check if touch is in image area
      const inImageArea = isTouchInImageArea(x, y);
      setIsTouchingImage(inImageArea);
      isTouchingImageRef.current = inImageArea;

      if (inImageArea) {
        // Prevent scrolling when touching the image
        e.preventDefault();

        // Direct DOM update for immediate visual response
        if (revealRef.current) {
          revealRef.current.style.clipPath = `ellipse(${120 * blobScale}px ${140 * blobScale}px at ${x}px ${y}px)`;
        }

        setMousePos({ x, y });
        setBlobPos({ x, y });
        setIsHovering(true);
        setShowMobileHint(false);
      }
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      if (!isTouchingImageRef.current) return;

      const touch = e.touches[0];
      const rect = container.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Check if still in image area
      const inImageArea = isTouchInImageArea(x, y);

      if (inImageArea) {
        // Prevent scrolling while touching image
        e.preventDefault();

        // Direct DOM update - bypasses React re-render completely!
        if (revealRef.current) {
          revealRef.current.style.clipPath = `ellipse(${120 * blobScale}px ${140 * blobScale}px at ${x}px ${y}px)`;
        }

        // Only update React state occasionally for trails (throttled to 20fps)
        const now = Date.now();
        if (now - lastOverlapCheck.current > 50) {
          lastOverlapCheck.current = now;
          setMousePos({ x, y });
          setBlobPos({ x, y });
        }
      } else {
        // User moved finger out of image area - stop the effect and allow scrolling
        setIsTouchingImage(false);
        isTouchingImageRef.current = false;
        setTimeout(() => {
          setIsHovering(false);
          setTrails([]);
        }, 300);
      }
    };

    const handleNativeTouchEnd = () => {
      if (isTouchingImageRef.current) {
        setIsTouchingImage(false);
        isTouchingImageRef.current = false;
        // Immediately hide the reveal
        setIsHovering(false);
        setTrails([]);
        // Also directly reset the clip-path for instant visual feedback
        if (revealRef.current) {
          revealRef.current.style.clipPath = 'ellipse(0px 0px at 0px 0px)';
        }
      }
    };

    // Add event listeners with passive: false to allow preventDefault
    container.addEventListener('touchstart', handleNativeTouchStart, { passive: false });
    container.addEventListener('touchmove', handleNativeTouchMove, { passive: false });
    container.addEventListener('touchend', handleNativeTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleNativeTouchStart);
      container.removeEventListener('touchmove', handleNativeTouchMove);
      container.removeEventListener('touchend', handleNativeTouchEnd);
    };
  }, [isTouchDevice, isTouchInImageArea, blobScale]);

  // Keep React handlers for mouse events only (touch is handled natively)
  const handleTouchStart = useCallback(() => {}, []);
  const handleTouchMove = useCallback(() => {}, []);
  const handleTouchEnd = useCallback(() => {}, []);

  const scrollToContent = () => {
    document.getElementById('about-content')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white">
      {/* Sticky Navigation - appears when scrolling past hero */}
      <nav
        className={`fixed top-0 right-0 z-50 p-6 transition-all duration-300 ${
          showStickyNav ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-end gap-2 bg-white/90 backdrop-blur-md rounded-xl p-4 shadow-lg border border-gray-100">
          {[
            { to: '/portfolio', label: 'Portfolio', primary: true },
            { to: '/solutions', label: 'Solutions' },
            { to: '/contact', label: 'Contact' },
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`group flex items-center gap-2 transition-all duration-300 ${
                link.primary
                  ? 'text-base font-medium text-gray-900 hover:text-[#0088cc]'
                  : 'text-sm font-medium text-gray-500 hover:text-[#0088cc]'
              }`}
            >
              <span className="relative">
                {link.label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#0088cc] transition-all duration-300 group-hover:w-full" />
              </span>
              {link.primary && (
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* Interactive Hero Section */}
      <div
        ref={containerRef}
        className={`personal-landing relative w-full h-screen overflow-hidden ${!isTouchDevice ? 'cursor-none' : ''}`}
        style={{
          touchAction: isTouchingImage ? 'none' : 'auto',
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Base layer - Adam Headshot */}
        <div
          className="absolute inset-0 flex items-center justify-center overflow-hidden"
          style={{
            transform: `translate(${parallax.x * 0.3}px, ${parallax.y * 0.3}px)`,
          }}
        >
          <div
            ref={imageAreaRef}
            className="relative"
            style={{
              touchAction: isTouchingImage ? 'none' : 'auto',
            }}
          >
            <img
              src="/Adam Headshot.png"
              alt=""
              className="headshot-img"
              style={{
                maxWidth: 'none',
                maskImage: 'radial-gradient(ellipse 55% 65% at 50% 55%, black 30%, rgba(0,0,0,0.3) 60%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(ellipse 55% 65% at 50% 55%, black 30%, rgba(0,0,0,0.3) 60%, transparent 80%)'
              }}
            />
          </div>
        </div>

        {/* White overlay for minimal aesthetic */}
        <div className="absolute inset-0 bg-white/75" />

        {/* Animated wave lines background */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-30"
          style={{
            transform: `translate(${parallax.x * 0.3}px, ${parallax.y * 0.3}px)`
          }}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0088cc" stopOpacity="0.4" />
              <stop offset="50%" stopColor="#4caf50" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#0088cc" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <path
              key={i}
              className="wave-line"
              d={`M -100 ${150 + i * 100} Q 300 ${100 + i * 100} 600 ${170 + i * 100} T 1200 ${150 + i * 100} T 1800 ${150 + i * 100} T 2400 ${150 + i * 100}`}
              fill="none"
              stroke="url(#waveGradient)"
              strokeWidth="1"
              style={{
                animationDelay: `${i * 0.8}s`,
              }}
            />
          ))}
        </svg>

        {/* SVG Filters for paint splatter gooey effect — keep overflow visible so
            clipPath/filter still resolve when the SVG has no layout box */}
        <svg
          className="absolute"
          width="0"
          height="0"
          aria-hidden="true"
          style={{ position: 'absolute', overflow: 'visible' }}
        >
          <defs>
            <filter id="goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 25 -12"
                result="goo"
              />
              <feComposite in="SourceGraphic" in2="goo" operator="atop" />
            </filter>
            <clipPath id="blobClip">
              <ellipse
                cx={blobPos.x}
                cy={blobPos.y}
                rx={isHovering ? 90 * blobScale : 0}
                ry={isHovering ? 110 * blobScale : 0}
              >
                {!isTouchDevice && (
                  <>
                    <animate attributeName="rx" values={isHovering ? `${85 * blobScale};${95 * blobScale};${85 * blobScale}` : "0;0;0"} dur="2.5s" repeatCount="indefinite" />
                    <animate attributeName="ry" values={isHovering ? `${105 * blobScale};${115 * blobScale};${105 * blobScale}` : "0;0;0"} dur="3s" repeatCount="indefinite" />
                  </>
                )}
              </ellipse>

              {isHovering && (
                <>
                  {/* Main splatter shapes - shown on all devices */}
                  <ellipse cx={blobPos.x - 60 * blobScale} cy={blobPos.y - 80 * blobScale} rx={35 * blobScale} ry={25 * blobScale}>
                    {!isTouchDevice && <animate attributeName="rx" values={`${30 * blobScale};${40 * blobScale};${30 * blobScale}`} dur="2.2s" repeatCount="indefinite" />}
                  </ellipse>
                  <circle cx={blobPos.x + 70 * blobScale} cy={blobPos.y - 90 * blobScale} r={28 * blobScale}>
                    {!isTouchDevice && <animate attributeName="r" values={`${25 * blobScale};${32 * blobScale};${25 * blobScale}`} dur="2.4s" repeatCount="indefinite" />}
                  </circle>
                  <ellipse cx={blobPos.x + 100 * blobScale} cy={blobPos.y + 20 * blobScale} rx={40 * blobScale} ry={30 * blobScale}>
                    {!isTouchDevice && <animate attributeName="rx" values={`${35 * blobScale};${45 * blobScale};${35 * blobScale}`} dur="2.6s" repeatCount="indefinite" />}
                  </ellipse>
                  <ellipse cx={blobPos.x - 95 * blobScale} cy={blobPos.y + 30 * blobScale} rx={32 * blobScale} ry={45 * blobScale}>
                    {!isTouchDevice && <animate attributeName="ry" values={`${40 * blobScale};${50 * blobScale};${40 * blobScale}`} dur="2.8s" repeatCount="indefinite" />}
                  </ellipse>
                  <ellipse cx={blobPos.x - 30 * blobScale} cy={blobPos.y + 100 * blobScale} rx={25 * blobScale} ry={45 * blobScale}>
                    {!isTouchDevice && <animate attributeName="ry" values={`${40 * blobScale};${52 * blobScale};${40 * blobScale}`} dur="2.3s" repeatCount="indefinite" />}
                  </ellipse>
                  <ellipse cx={blobPos.x + 40 * blobScale} cy={blobPos.y + 90 * blobScale} rx={20 * blobScale} ry={38 * blobScale}>
                    {!isTouchDevice && <animate attributeName="ry" values={`${35 * blobScale};${42 * blobScale};${35 * blobScale}`} dur="2.7s" repeatCount="indefinite" />}
                  </ellipse>

                  {/* Additional detail shapes - desktop only for performance */}
                  {!isTouchDevice && (
                    <>
                      <circle cx={blobPos.x - 95 * blobScale} cy={blobPos.y - 55 * blobScale} r={18 * blobScale}>
                        <animate attributeName="r" values={`${15 * blobScale};${22 * blobScale};${15 * blobScale}`} dur="1.8s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={blobPos.x + 110 * blobScale} cy={blobPos.y - 60 * blobScale} r={15 * blobScale}>
                        <animate attributeName="r" values={`${12 * blobScale};${18 * blobScale};${12 * blobScale}`} dur="1.9s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={blobPos.x + 130 * blobScale} cy={blobPos.y - 15 * blobScale} r={20 * blobScale}>
                        <animate attributeName="r" values={`${17 * blobScale};${24 * blobScale};${17 * blobScale}`} dur="2.1s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={blobPos.x - 120 * blobScale} cy={blobPos.y - 10 * blobScale} r={16 * blobScale}>
                        <animate attributeName="r" values={`${13 * blobScale};${19 * blobScale};${13 * blobScale}`} dur="2s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={blobPos.x + 80 * blobScale} cy={blobPos.y + 75 * blobScale} r={22 * blobScale}>
                        <animate attributeName="r" values={`${19 * blobScale};${26 * blobScale};${19 * blobScale}`} dur="2.2s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={blobPos.x - 70 * blobScale} cy={blobPos.y + 80 * blobScale} r={18 * blobScale}>
                        <animate attributeName="r" values={`${15 * blobScale};${21 * blobScale};${15 * blobScale}`} dur="1.9s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={blobPos.x - 130 * blobScale} cy={blobPos.y - 30 * blobScale} r={8 * blobScale} />
                      <circle cx={blobPos.x + 140 * blobScale} cy={blobPos.y + 50 * blobScale} r={10 * blobScale} />
                      <circle cx={blobPos.x - 50 * blobScale} cy={blobPos.y + 130 * blobScale} r={12 * blobScale} />
                      <circle cx={blobPos.x + 20 * blobScale} cy={blobPos.y + 140 * blobScale} r={9 * blobScale} />
                      <circle cx={blobPos.x + 100 * blobScale} cy={blobPos.y + 110 * blobScale} r={11 * blobScale} />
                    </>
                  )}
                </>
              )}
            </clipPath>
          </defs>
        </svg>

        {/* Reveal layer - CSS clip-path for GPU acceleration on mobile */}
        <div
          ref={revealRef}
          className="absolute inset-0 pointer-events-none"
          style={{
            // On mobile: initial state (updated directly via ref during touch)
            // On desktop: SVG clip-path with goo filter
            clipPath: isTouchDevice
              ? `ellipse(${isHovering ? 120 * blobScale : 0}px ${isHovering ? 140 * blobScale : 0}px at ${blobPos.x}px ${blobPos.y}px)`
              : 'url(#blobClip)',
            filter: isTouchDevice ? 'none' : 'url(#goo)',
            willChange: isTouchDevice ? 'clip-path' : 'auto',
            transform: 'translateZ(0)',
          }}
        >
          <div
            className="absolute inset-0 flex items-center justify-center overflow-hidden"
            style={{
              transform: `translate(${parallax.x * -0.3}px, ${parallax.y * -0.3}px) translateZ(0)`
            }}
          >
            {/* Mirror headshot wrapper so both images share the same centering box */}
            <div className="relative">
              <img
                src="/Adam Tech Terminator.png"
                alt=""
                className="astronaut-img"
                style={{ maxWidth: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* Trail splatter droplets */}
        {trails.map((trail) => (
          <div
            key={trail.id}
            className="absolute pointer-events-none"
            style={{
              left: trail.x,
              top: trail.y,
              transform: `translate(-50%, -50%) rotate(${trail.rotation}deg)`,
              opacity: trail.opacity,
            }}
          >
            {/* Main splatter body glow */}
            <div
              className="absolute"
              style={{
                width: `${100 * trail.scale}px`,
                height: `${120 * trail.scale}px`,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'radial-gradient(ellipse 50% 60% at 50% 45%, rgba(0,136,204,0.3) 0%, rgba(76,175,80,0.12) 60%, transparent 80%)',
                filter: 'blur(8px)',
                borderRadius: '40% 60% 35% 65% / 55% 35% 65% 45%',
              }}
            />
            {/* Splatter droplets - varies by variant */}
            {trail.variant === 0 && (
              <>
                <div className="absolute" style={{ width: `${25 * trail.scale}px`, height: `${20 * trail.scale}px`, top: `${-35 * trail.scale}px`, left: `${-20 * trail.scale}px`, borderRadius: '50%', background: 'rgba(0,136,204,0.25)', filter: 'blur(4px)' }} />
                <div className="absolute" style={{ width: `${15 * trail.scale}px`, height: `${15 * trail.scale}px`, top: `${-25 * trail.scale}px`, left: `${15 * trail.scale}px`, borderRadius: '50%', background: 'rgba(76,175,80,0.2)', filter: 'blur(3px)' }} />
                <div className="absolute" style={{ width: `${20 * trail.scale}px`, height: `${30 * trail.scale}px`, bottom: `${-30 * trail.scale}px`, left: `${-10 * trail.scale}px`, borderRadius: '40% 60% 50% 50%', background: 'rgba(0,136,204,0.2)', filter: 'blur(5px)' }} />
                <div className="absolute" style={{ width: `${12 * trail.scale}px`, height: `${12 * trail.scale}px`, top: `${10 * trail.scale}px`, right: `${-25 * trail.scale}px`, borderRadius: '50%', background: 'rgba(76,175,80,0.22)', filter: 'blur(3px)' }} />
              </>
            )}
            {trail.variant === 1 && (
              <>
                <div className="absolute" style={{ width: `${18 * trail.scale}px`, height: `${25 * trail.scale}px`, top: `${-30 * trail.scale}px`, right: `${-15 * trail.scale}px`, borderRadius: '50% 50% 40% 60%', background: 'rgba(0,136,204,0.22)', filter: 'blur(4px)' }} />
                <div className="absolute" style={{ width: `${22 * trail.scale}px`, height: `${18 * trail.scale}px`, bottom: `${-25 * trail.scale}px`, right: `${5 * trail.scale}px`, borderRadius: '50%', background: 'rgba(76,175,80,0.2)', filter: 'blur(4px)' }} />
                <div className="absolute" style={{ width: `${10 * trail.scale}px`, height: `${10 * trail.scale}px`, top: `${-15 * trail.scale}px`, left: `${-30 * trail.scale}px`, borderRadius: '50%', background: 'rgba(0,136,204,0.25)', filter: 'blur(2px)' }} />
                <div className="absolute" style={{ width: `${15 * trail.scale}px`, height: `${20 * trail.scale}px`, bottom: `${-20 * trail.scale}px`, left: `${-20 * trail.scale}px`, borderRadius: '45% 55% 50% 50%', background: 'rgba(76,175,80,0.18)', filter: 'blur(4px)' }} />
              </>
            )}
            {trail.variant === 2 && (
              <>
                <div className="absolute" style={{ width: `${20 * trail.scale}px`, height: `${15 * trail.scale}px`, top: `${-28 * trail.scale}px`, left: `${0}px`, borderRadius: '50%', background: 'rgba(76,175,80,0.23)', filter: 'blur(3px)' }} />
                <div className="absolute" style={{ width: `${14 * trail.scale}px`, height: `${22 * trail.scale}px`, bottom: `${-28 * trail.scale}px`, right: `${-18 * trail.scale}px`, borderRadius: '50% 50% 45% 55%', background: 'rgba(0,136,204,0.22)', filter: 'blur(4px)' }} />
                <div className="absolute" style={{ width: `${18 * trail.scale}px`, height: `${18 * trail.scale}px`, top: `${5 * trail.scale}px`, left: `${-35 * trail.scale}px`, borderRadius: '50%', background: 'rgba(0,136,204,0.2)', filter: 'blur(3px)' }} />
                <div className="absolute" style={{ width: `${12 * trail.scale}px`, height: `${16 * trail.scale}px`, bottom: `${-15 * trail.scale}px`, left: `${10 * trail.scale}px`, borderRadius: '50%', background: 'rgba(76,175,80,0.2)', filter: 'blur(3px)' }} />
              </>
            )}
            {/* Central reveal with astronaut image */}
            <div
              style={{
                width: `${70 * trail.scale}px`,
                height: `${85 * trail.scale}px`,
                borderRadius: '45% 55% 40% 60% / 50% 45% 55% 50%',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                className="absolute bg-no-repeat astronaut-bg"
                style={{
                  width: '100vw',
                  height: '100vh',
                  backgroundImage: 'url("/Adam Tech Terminator.png")',
                  left: `calc(-${trail.x}px + ${35 * trail.scale}px)`,
                  top: `calc(-${trail.y}px + ${42 * trail.scale}px)`,
                  transform: `rotate(-${trail.rotation}deg)`,
                  maskImage: 'radial-gradient(ellipse 70% 75% at 50% 50%, black 60%, transparent 100%)',
                  WebkitMaskImage: 'radial-gradient(ellipse 70% 75% at 50% 50%, black 60%, transparent 100%)',
                }}
              />
            </div>
          </div>
        ))}

        {/* Main splatter cursor glow */}
        {isHovering && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: blobPos.x,
              top: blobPos.y,
              transform: 'translate(-50%, -50%)',
              width: '350px',
              height: '380px',
            }}
          >
            <div
              className="absolute animate-blob-morph"
              style={{
                width: '100%',
                height: '100%',
                background: 'radial-gradient(ellipse 45% 55% at 50% 45%, rgba(0,136,204,0.12) 0%, transparent 70%)',
                borderRadius: '40% 60% 35% 65% / 55% 35% 65% 45%',
                filter: 'blur(25px)',
              }}
            />
            <div
              className="absolute animate-blob-morph-reverse"
              style={{
                width: '45%',
                height: '35%',
                top: '-15%',
                left: '10%',
                background: 'radial-gradient(ellipse at center, rgba(0,136,204,0.1) 0%, transparent 70%)',
                borderRadius: '50% 50% 40% 60% / 60% 40% 60% 40%',
                filter: 'blur(15px)',
              }}
            />
            <div
              className="absolute"
              style={{
                width: '40%',
                height: '50%',
                bottom: '-10%',
                left: '25%',
                background: 'radial-gradient(ellipse at center, rgba(76,175,80,0.08) 0%, transparent 70%)',
                borderRadius: '45% 55% 50% 50% / 40% 60% 40% 60%',
                filter: 'blur(18px)',
                animation: 'blob-morph 6s ease-in-out infinite reverse',
              }}
            />
            <div
              className="absolute"
              style={{
                width: '35%',
                height: '30%',
                top: '20%',
                right: '-10%',
                background: 'radial-gradient(ellipse at center, rgba(0,136,204,0.08) 0%, transparent 70%)',
                borderRadius: '60% 40% 55% 45% / 45% 55% 45% 55%',
                filter: 'blur(12px)',
                animation: 'blob-morph-reverse 5s ease-in-out infinite',
              }}
            />
          </div>
        )}

        {/* Name/Logo - Top Left */}
        <div
          className="absolute top-6 sm:top-8 left-6 sm:left-8 z-10"
          style={{
            transform: `translate(${parallax.x * -0.3}px, ${parallax.y * -0.3}px)`
          }}
        >
          <div className="relative">
            {/* Backdrop glow when inverted */}
            <div
              className={`absolute -inset-4 rounded-2xl transition-all duration-300 ${
                inversions.name ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                background: 'radial-gradient(ellipse at center, rgba(0,136,204,0.4) 0%, rgba(76,175,80,0.2) 50%, transparent 70%)',
                filter: 'blur(20px)',
              }}
            />
            <h1
              ref={nameRef}
              className={`relative font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight transition-colors duration-300 ${
                inversions.name ? 'text-white drop-shadow-[0_2px_10px_rgba(0,136,204,0.5)]' : 'text-gray-900'
              }`}
            >
              <span className="block">Adam</span>
              <span className="block">Schwartz</span>
            </h1>
          </div>
        </div>

        {/* Navigation Links - Top Right */}
        <nav
          ref={portfolioRef}
          className="absolute top-6 sm:top-8 right-6 sm:right-8 z-10"
          style={{
            transform: `translate(${parallax.x * -0.3}px, ${parallax.y * -0.3}px)`
          }}
        >
          <div className="relative">
            {/* Backdrop glow when inverted */}
            <div
              className={`absolute -inset-4 rounded-2xl transition-all duration-300 ${
                inversions.portfolio ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                background: 'radial-gradient(ellipse at center, rgba(0,136,204,0.4) 0%, rgba(76,175,80,0.2) 50%, transparent 70%)',
                filter: 'blur(20px)',
              }}
            />
            <div className="relative flex flex-col items-end gap-1 sm:gap-2">
              {[
                { to: '/portfolio', label: 'Portfolio', primary: true },
                { to: '/solutions', label: 'Solutions' },
                { to: '/contact', label: 'Contact' },
              ].map((link, index) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`group flex items-center gap-1 sm:gap-2 transition-all duration-300 ${
                    link.primary
                      ? `text-sm sm:text-lg font-medium tracking-wide ${inversions.portfolio ? 'text-white drop-shadow-[0_2px_8px_rgba(0,136,204,0.5)]' : 'text-gray-900'}`
                      : `text-xs sm:text-sm font-medium tracking-wide ${inversions.portfolio ? 'text-white/80 drop-shadow-[0_2px_6px_rgba(0,136,204,0.4)]' : 'text-gray-500'}`
                  } hover:text-[#0088cc]`}
                  style={{
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <span className="relative">
                    {link.label}
                    <span
                      className="absolute -bottom-0.5 left-0 w-0 h-px bg-current transition-all duration-300 group-hover:w-full"
                    />
                  </span>
                  {link.primary && (
                    <svg
                      className="w-3 h-3 sm:w-4 sm:h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* Social Icons - Bottom Right */}
        <div
          ref={socialRef}
          className="absolute bottom-6 sm:bottom-8 right-6 sm:right-8 z-10 flex gap-2 sm:gap-4"
          style={{
            transform: `translate(${parallax.x * -0.3}px, ${parallax.y * -0.3}px)`
          }}
        >
          <a
            href="https://x.com/Vitzer1001011"
            target="_blank"
            rel="noopener noreferrer"
            className={`w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 ${
              socialHovered === 'x' && inversions.social
                ? 'bg-gray-900 text-white'
                : inversions.social
                  ? 'bg-white text-gray-900'
                  : 'bg-gray-900 text-white'
            }`}
            aria-label="X (Twitter)"
            onMouseEnter={() => setSocialHovered('x')}
            onMouseLeave={() => setSocialHovered(null)}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>

          <a
            href="https://github.com/adamsch0100"
            target="_blank"
            rel="noopener noreferrer"
            className={`w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 ${
              socialHovered === 'github' && inversions.social
                ? 'bg-gray-900 text-white'
                : inversions.social
                  ? 'bg-white text-gray-900'
                  : 'bg-gray-900 text-white'
            }`}
            aria-label="GitHub"
            onMouseEnter={() => setSocialHovered('github')}
            onMouseLeave={() => setSocialHovered(null)}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
            </svg>
          </a>
        </div>

        {/* E8 Solutions Logo - Bottom Left with Particle Explosion */}
        <div
          className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 z-10"
          style={{
            transform: `translate(${parallax.x * -0.3}px, ${parallax.y * -0.3}px)`
          }}
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
        >
          <Link
            to="/"
            className="group relative flex items-center gap-3 sm:gap-4 transition-all duration-300"
          >
            {/* Container for logo with effects */}
            <div className="relative flex items-center justify-center min-w-[40px] min-h-[40px] sm:min-w-[48px] sm:min-h-[48px]">
              {/* Particle explosion effect */}
              {(logoHovered || inversions.hint) && (
                <>
                  {/* Orbiting particles */}
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 rounded-full"
                      style={{
                        background: i % 2 === 0 ? '#0088cc' : '#4caf50',
                        left: '50%',
                        top: '50%',
                        transform: `rotate(${i * 45}deg) translateX(${logoHovered ? 40 : 25}px)`,
                        opacity: logoHovered ? 0.9 : 0.5,
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transitionDelay: `${i * 30}ms`,
                        boxShadow: `0 0 ${logoHovered ? 8 : 4}px ${i % 2 === 0 ? '#0088cc' : '#4caf50'}`,
                      }}
                    />
                  ))}
                  {/* Secondary ring of smaller particles */}
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`s-${i}`}
                      className="absolute rounded-full"
                      style={{
                        width: logoHovered ? '4px' : '2px',
                        height: logoHovered ? '4px' : '2px',
                        background: i % 3 === 0 ? '#0088cc' : i % 3 === 1 ? '#4caf50' : '#fff',
                        left: '50%',
                        top: '50%',
                        transform: `rotate(${i * 30 + 15}deg) translateX(${logoHovered ? 55 : 35}px)`,
                        opacity: logoHovered ? 0.7 : 0.3,
                        transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transitionDelay: `${i * 25}ms`,
                      }}
                    />
                  ))}
                  {/* Radial glow */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `radial-gradient(circle at center, rgba(0,136,204,${logoHovered ? 0.4 : 0.2}) 0%, rgba(76,175,80,${logoHovered ? 0.2 : 0.1}) 50%, transparent 70%)`,
                      transform: `scale(${logoHovered ? 2.5 : 1.8})`,
                      transition: 'all 0.4s ease-out',
                      filter: 'blur(8px)',
                    }}
                  />
                </>
              )}

              {/* Main logo */}
              <img
                src="/e8solutionslogo.png"
                alt="E8 Solutions"
                className="h-10 sm:h-12 w-auto max-w-none transition-all duration-300"
                style={{
                  filter: inversions.hint
                    ? 'brightness(1.5) drop-shadow(0 0 12px rgba(0,136,204,0.8))'
                    : logoHovered
                      ? 'drop-shadow(0 0 8px rgba(0,136,204,0.5))'
                      : 'none',
                  transform: logoHovered ? 'scale(1.1) rotate(3deg)' : 'scale(1)',
                  opacity: inversions.hint || logoHovered ? 1 : 0.6,
                }}
              />
            </div>

            {/* Text with glow effect - hidden on mobile */}
            <span
              className="relative font-medium tracking-wide transition-all duration-300 hidden sm:inline text-sm sm:text-base"
              style={{
                color: inversions.hint ? '#fff' : logoHovered ? '#0088cc' : 'rgb(107, 114, 128)',
                textShadow: inversions.hint
                  ? '0 0 20px rgba(0,136,204,0.8), 0 0 40px rgba(76,175,80,0.4)'
                  : logoHovered
                    ? '0 0 10px rgba(0,136,204,0.3)'
                    : 'none',
                transform: logoHovered ? 'translateX(4px)' : 'translateX(0)',
              }}
            >
              E8 Solutions
              {/* Animated underline */}
              <span
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-[#0088cc] to-[#4caf50] transition-all duration-300"
                style={{
                  width: logoHovered ? '100%' : '0%',
                }}
              />
            </span>
          </Link>
        </div>

        {/* Scroll indicator - repositioned for mobile */}
        <button
          ref={hintRef}
          onClick={scrollToContent}
          className={`absolute bottom-6 sm:bottom-8 left-1/2 z-10 flex flex-col items-center gap-1 sm:gap-2 transition-colors duration-300 cursor-pointer hover:opacity-70 ${
            inversions.hint ? 'text-white' : 'text-gray-400'
          }`}
          style={{
            transform: `translateX(-50%) translate(${parallax.x * -0.3}px, ${parallax.y * -0.3}px)`
          }}
        >
          <span className="text-xs sm:text-sm tracking-wide hidden sm:block">Scroll to explore</span>
          <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 animate-bounce" />
        </button>

        {/* Mobile touch hint - shown only on touch devices */}
        {isTouchDevice && showMobileHint && (
          <div
            className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 sm:hidden"
            style={{
              animation: 'fadeIn 0.5s ease-out'
            }}
          >
            <div className="flex flex-col items-center gap-1 text-gray-400">
              <span className="text-xs tracking-wide">Touch image to reveal</span>
              <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* About Content Sections */}
      <div id="about-content">
        {/* Intro Section - Minimal with animated elements */}
        <section className="relative py-24 bg-white overflow-hidden">
          {/* Animated background decorations */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-20 right-10 w-64 h-64 rounded-full opacity-30"
              style={{
                background: 'radial-gradient(circle, rgba(0,136,204,0.15) 0%, transparent 70%)',
                animation: 'blob-morph 8s ease-in-out infinite'
              }}
            />
            <div
              className="absolute bottom-20 left-10 w-48 h-48 rounded-full opacity-20"
              style={{
                background: 'radial-gradient(circle, rgba(76,175,80,0.2) 0%, transparent 70%)',
                animation: 'blob-morph-reverse 10s ease-in-out infinite'
              }}
            />
            {/* Subtle grid pattern */}
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: 'linear-gradient(#0088cc 1px, transparent 1px), linear-gradient(90deg, #0088cc 1px, transparent 1px)',
                backgroundSize: '60px 60px'
              }}
            />
          </div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full mb-8">
                <MapPin className="w-4 h-4 text-[#0088cc]" />
                <span className="text-sm text-gray-600 font-medium">Colorado, USA</span>
              </div>

              <h2 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Full-Stack Developer
                <span className="block text-[#0088cc]">& AI Enthusiast</span>
              </h2>
              <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
                I combine modern web technologies with AI-powered development to ship
                quality software faster than ever before.
              </p>
            </div>

            {/* Stats with animated borders */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto">
              {[
                { value: '5+', label: 'Years Coding', color: '#0088cc' },
                { value: '10+', label: 'Projects Shipped', color: '#4caf50' },
                { value: '100%', label: 'AI-Enhanced', color: '#0088cc' }
              ].map((stat, index) => (
                <div
                  key={stat.label}
                  className="relative group"
                >
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(135deg, ${stat.color}20, transparent)`,
                    }}
                  />
                  <div className="relative p-6 text-center">
                    <div
                      className="text-4xl md:text-5xl font-bold mb-2"
                      style={{ color: stat.color }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-500 font-medium">{stat.label}</div>
                  </div>
                  {/* Animated corner accent */}
                  <div
                    className="absolute top-0 right-0 w-8 h-8 opacity-20"
                    style={{
                      background: `linear-gradient(135deg, ${stat.color}, transparent)`,
                      borderRadius: '0 1rem 0 1rem',
                      animation: `blob-pulse ${2 + index * 0.3}s ease-in-out infinite`
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Teaser - See What I'm Building */}
        <PortfolioTeaser />

        {/* Build With Us - How I Can Help */}
        <BuildWithUs />

        {/* About Me Section - Combined Story + Values */}
        <section className="relative py-24 bg-gray-900 text-white overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="aboutGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#0088cc" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#4caf50" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#0088cc" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              {[0, 1, 2].map((i) => (
                <path
                  key={i}
                  className="wave-line"
                  d={`M -100 ${100 + i * 100} Q 300 ${60 + i * 100} 600 ${120 + i * 100} T 1200 ${100 + i * 100} T 1800 ${100 + i * 100}`}
                  fill="none"
                  stroke="url(#aboutGradient)"
                  strokeWidth="1"
                  style={{ animationDelay: `${i * 0.6}s` }}
                />
              ))}
            </svg>
          </div>

          {/* Blob decorations */}
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-[#0088cc]/10 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-[#4caf50]/10 blur-3xl" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-semibold text-[#0088cc] bg-[#0088cc]/20 rounded-full uppercase tracking-wider mb-4">
                About Me
              </span>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold">Who I Am & What I Value</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left - Story */}
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  I started coding because I wanted to build things - real products that people use every day.
                  Over the years, I've shipped everything from small business tools to complex SaaS platforms.
                </p>
                <p>
                  When AI coding tools like <span className="text-[#0088cc] font-medium">Claude Code</span> and <span className="text-[#0088cc] font-medium">Cursor</span> emerged,
                  I saw an opportunity to work at a higher level - focusing on architecture and user experience
                  while AI handles implementation details.
                </p>
                <p>
                  Today, I combine years of experience with cutting-edge AI tools to deliver projects
                  that would typically take months in just weeks.
                </p>
              </div>

              {/* Right - Values */}
              <div className="space-y-4">
                {[
                  { icon: Zap, title: 'Ship It', description: 'Perfect is the enemy of done. Deliver working software, then iterate.', color: '#0088cc' },
                  { icon: Target, title: 'Quality Code', description: 'Fast doesn\'t mean sloppy. Clean architecture and maintainable code.', color: '#4caf50' },
                  { icon: Heart, title: 'Honest Work', description: 'Clear communication, realistic timelines, and no surprises.', color: '#0088cc' }
                ].map((value) => (
                  <div
                    key={value.title}
                    className="group flex items-start gap-4 p-5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${value.color}20` }}
                    >
                      <value.icon className="w-5 h-5" style={{ color: value.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{value.title}</h3>
                      <p className="text-sm text-gray-400">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Clean minimal */}
        <section className="relative py-24 bg-white overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]"
              style={{
                background: 'radial-gradient(circle, rgba(0,136,204,0.05) 0%, transparent 50%)',
              }}
            />
          </div>

          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-playfair text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Let's Build Something
              <span className="block text-[#0088cc]">Together</span>
            </h2>
            <p className="text-lg text-gray-500 mb-10 max-w-xl mx-auto">
              Whether you have a specific project in mind or just want to explore possibilities,
              I'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-full hover:bg-[#0088cc] transition-all duration-300 font-medium text-lg"
              >
                Start a Conversation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-gray-900 rounded-full border-2 border-gray-200 hover:border-[#0088cc] hover:text-[#0088cc] transition-all duration-300 font-medium text-lg"
              >
                View My Work
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PersonalLanding;
